<template>
  <div class="ai-table-block">
    <div v-if="block.title" class="block-title">{{ block.title }}</div>
    <div v-if="columns.length" class="table-wrap">
      <table class="ai-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="{ metric: col.kind === 'metric' }"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in block.rows" :key="idx">
            <td
              v-for="col in columns"
              :key="col.key"
              :class="{ metric: col.kind === 'metric' }"
            >
              {{ cellValue(row, col) }}
            </td>
          </tr>
          <tr v-if="hasTotals" class="totals-row">
            <td :colspan="dimensionCount">Итого</td>
            <td
              v-for="col in metricColumns"
              :key="col.key"
              class="metric"
            >
              {{ cellValue(block.totals, col) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="block-empty">Пустая таблица</div>
    <div class="block-meta">
      <span v-if="block.row_count !== undefined">
        строк: {{ block.row_count.toLocaleString('ru-RU') }}
      </span>
      <span v-if="block.truncated" class="truncated">показаны не все</span>
    </div>
    <details v-if="block.sql" class="sql-details">
      <summary>SQL</summary>
      <pre>{{ block.sql }}</pre>
    </details>
  </div>
</template>

<script>
import { computed } from 'vue';
import { formatterByName } from '../format';

export default {
  name: 'AiTableBlock',
  props: {
    // block: type=table → columns [{key,label,kind,format}], rows [{key:value}]
    //        type=sql_table → columns [str], rows [[]]
    block: { type: Object, required: true },
  },
  setup(props) {
    const columns = computed(() => {
      const raw = props.block.columns || [];
      return raw.map((col) =>
        typeof col === 'string'
          ? { key: col, label: col, kind: 'plain' }
          : col
      );
    });
    const metricColumns = computed(() =>
      columns.value.filter((col) => col.kind === 'metric')
    );
    const dimensionCount = computed(
      () => columns.value.length - metricColumns.value.length
    );
    const hasTotals = computed(
      () =>
        props.block.totals &&
        Object.keys(props.block.totals).length > 0 &&
        metricColumns.value.length > 0
    );

    const cellValue = (row, col) => {
      if (row === null || row === undefined) return '—';
      // sql_table: row — массив; для него key — исходная строка-имя,
      // поэтому индекс берём по позиции колонки.
      if (Array.isArray(row)) {
        const index = columns.value.findIndex((c) => c.key === col.key);
        const value = row[index];
        return value === null || value === undefined
          ? '—'
          : typeof value === 'number'
            ? value.toLocaleString('ru-RU', { maximumFractionDigits: 4 })
            : String(value);
      }
      const value = row[col.key];
      if (value === null || value === undefined) return '—';
      if (col.kind === 'metric' && col.format) {
        return formatterByName(col.format)(value);
      }
      if (typeof value === 'number') {
        return value.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
      }
      return String(value);
    };

    return { columns, metricColumns, dimensionCount, hasTotals, cellValue };
  },
};
</script>

<style scoped>
.ai-table-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 10px 12px;
  overflow: hidden;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.table-wrap {
  overflow-x: auto;
  max-height: 420px;
  overflow-y: auto;
}

.ai-table {
  border-collapse: collapse;
  font-size: 12px;
  width: 100%;
}

.ai-table th,
.ai-table td {
  border-bottom: 1px solid #f1f3f5;
  padding: 5px 10px;
  text-align: left;
  white-space: nowrap;
}

.ai-table th {
  position: sticky;
  top: 0;
  background: #f9fafb;
  color: #475467;
  font-weight: 600;
  z-index: 1;
}

.ai-table td.metric,
.ai-table th.metric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.totals-row td {
  border-top: 2px solid #e5e7eb;
  font-weight: 600;
  background: #fafbfc;
}

.block-empty {
  color: #98a2b3;
  font-size: 13px;
  padding: 8px 0;
}

.block-meta {
  display: flex;
  gap: 10px;
  margin-top: 6px;
  font-size: 11px;
  color: #98a2b3;
}

.truncated {
  color: #f79009;
}

.sql-details {
  margin-top: 6px;
  font-size: 11px;
  color: #6b7280;
}

.sql-details pre {
  margin-top: 4px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
}
</style>
